import React, { useEffect, useState, Children } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { UploadIcon, XIcon, CheckCircleIcon, UserPlusIcon, MailIcon, ClockIcon, ActivityIcon, TrendingUpIcon, UsersIcon, FileTextIcon, SearchIcon, ArrowRightIcon, BuildingIcon, SaveIcon, SendIcon, EyeIcon, MonitorIcon, SmartphoneIcon, GlobeIcon, MapPinIcon, MailIcon as EmailIcon, FacebookIcon, InstagramIcon, LinkedinIcon, YoutubeIcon, CheckCircle2Icon, CircleIcon, NetworkIcon, ChevronRightIcon, CreditCardIcon, ArrowLeftIcon } from 'lucide-react';
import { Exhibitor } from '../../../data/exhibitors';
interface ExhibitorProfilePageProps {
  exhibitor: Exhibitor;
  onBack: () => void;
  onSave: (updatedExhibitor: Partial<Exhibitor>) => void;
  allExhibitors?: Exhibitor[];
  onNavigateToExhibitor?: (exhibitorId: string) => void;
}
type TabType = 'overview' | 'team' | 'passes' | 'profile';
type PreviewType = 'website' | 'app';
interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  type: 'lead' | 'associated';
  lastLogin?: string;
  status: 'active' | 'pending' | 'inactive';
}
// Mock parent/child exhibitor data
const mockParentExhibitor = {
  id: 'parent-1',
  name: 'Global Fitness Corp',
  standNumber: 'A100'
};
const mockChildExhibitors = [{
  id: 'child-1',
  name: 'FitTech Solutions',
  standNumber: 'A101'
}, {
  id: 'child-2',
  name: 'Wellness Innovations',
  standNumber: 'A102'
}];
const COUNTRIES = ['UAE', 'Saudi Arabia', 'Kuwait', 'Qatar', 'Bahrain', 'Oman', 'USA', 'UK', 'Germany', 'France', 'Italy', 'Spain', 'China', 'Japan', 'India', 'Australia', 'Canada', 'Brazil', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Poland', 'Turkey', 'Egypt', 'South Africa', 'Russia', 'Hungary', 'New Zealand', 'Pakistan'];
const INDUSTRIES = ['Apparel', 'Association/Council', 'Beauty', 'Beauty & Wellness', 'Consultant', 'Distributor', 'Education & Training', 'Equipment', 'Equipment Distributor', 'Fitness', 'Fitness Franchise', 'Government', 'Gym', 'Gym Accessories', 'Healthcare', 'Hospitality', 'Media', 'Nutrition', 'Real Estate', 'Others', 'Pharmacy', 'Recovery', 'Retailer FMCG', 'Supplement Distributor', 'Spa and Wellness', 'Technology', 'Training Provider', 'Venture Capitalist & Private Investor', 'Wellness'];
// Mock team members data
const mockTeamMembers: TeamMember[] = [{
  id: '1',
  name: 'Sarah Johnson',
  email: 'sarah@orbit4.org',
  role: 'Marketing Director',
  type: 'lead',
  lastLogin: '2 hours ago',
  status: 'active'
}, {
  id: '2',
  name: 'Mike Chen',
  email: 'mike@orbit4.org',
  role: 'Sales Manager',
  type: 'lead',
  lastLogin: '1 day ago',
  status: 'active'
}, {
  id: '3',
  name: 'Emma Wilson',
  email: 'emma@orbit4.org',
  role: 'Brand Coordinator',
  type: 'associated',
  lastLogin: '3 days ago',
  status: 'active'
}, {
  id: '4',
  name: 'David Brown',
  email: 'david@orbit4.org',
  role: 'Operations',
  type: 'associated',
  lastLogin: 'Never',
  status: 'pending'
}];
// Mock recent activity
const mockRecentActivity = [{
  id: '1',
  user: 'Sarah Johnson',
  action: 'Updated brand logo',
  time: '2 hours ago'
}, {
  id: '2',
  user: 'Mike Chen',
  action: 'Completed brand profile',
  time: '1 day ago'
}, {
  id: '3',
  user: 'Sarah Johnson',
  action: 'Added social media links',
  time: '2 days ago'
}, {
  id: '4',
  user: 'Emma Wilson',
  action: 'Updated brand overview',
  time: '3 days ago'
}];
export function ExhibitorProfilePage({
  exhibitor,
  onBack,
  onSave,
  allExhibitors = [],
  onNavigateToExhibitor
}: ExhibitorProfilePageProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'team' | 'passes' | 'profile'>('overview');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(mockTeamMembers);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewType>('website');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteType, setInviteType] = useState<'lead' | 'associated'>('associated');
  // Form state for Brand Profile tab
  const [brandName, setBrandName] = useState(exhibitor.name);
  const [website, setWebsite] = useState(exhibitor.website);
  const [headquarters, setHeadquarters] = useState(exhibitor.headquarters || '');
  const [otherTerritories, setOtherTerritories] = useState<string[]>([]);
  const [email, setEmail] = useState(exhibitor.email || '');
  const [overview, setOverview] = useState(exhibitor.description);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(exhibitor.industries || []);
  const [brandLogo, setBrandLogo] = useState(exhibitor.logo);
  const [socialFacebook, setSocialFacebook] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialLinkedin, setSocialLinkedin] = useState('');
  const [socialYoutube, setSocialYoutube] = useState('');
  const [socialTiktok, setSocialTiktok] = useState('');
  const [declaration, setDeclaration] = useState(false);
  // UI state
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showTerritoriesDropdown, setShowTerritoriesDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [territoriesSearch, setTerritoriesSearch] = useState('');
  // Mock progress data
  const brandProfileProgress = 75;
  const exhibitorPortalProgress = 45;
  // Calculate if publish should be enabled (100% complete)
  const isPublishDisabled = brandProfileProgress < 100;
  // Load existing social links
  useEffect(() => {
    if (exhibitor.socials) {
      exhibitor.socials.forEach(social => {
        if (social.logo === 'facebook') setSocialFacebook(social.link);
        if (social.logo === 'instagram') setSocialInstagram(social.link);
        if (social.logo === 'linkedin') setSocialLinkedin(social.link);
        if (social.logo === 'youtube') setSocialYoutube(social.link);
        if (social.logo === 'tiktok') setSocialTiktok(social.link);
      });
    }
  }, [exhibitor]);
  const toggleIndustry = (industry: string) => {
    setSelectedIndustries(prev => prev.includes(industry) ? prev.filter(i => i !== industry) : [...prev, industry]);
  };
  const toggleTerritory = (country: string) => {
    setOtherTerritories(prev => prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]);
  };
  const handleSaveDraft = () => {
    const socials = [socialFacebook && {
      logo: 'facebook',
      link: socialFacebook
    }, socialInstagram && {
      logo: 'instagram',
      link: socialInstagram
    }, socialLinkedin && {
      logo: 'linkedin',
      link: socialLinkedin
    }, socialYoutube && {
      logo: 'youtube',
      link: socialYoutube
    }, socialTiktok && {
      logo: 'tiktok',
      link: socialTiktok
    }].filter(Boolean) as {
      logo: string;
      link: string;
    }[];
    onSave({
      name: brandName,
      website,
      headquarters: headquarters || null,
      email: email || null,
      description: overview,
      industries: selectedIndustries,
      logo: brandLogo,
      socials: socials.length > 0 ? socials : null
    });
    console.log('Draft saved');
  };
  const handlePublish = () => {
    handleSaveDraft();
    console.log('Profile published to website and app');
    // This would trigger the publish action
  };
  const handleInviteTeamMember = () => {
    const newMember: TeamMember = {
      id: `new-${Date.now()}`,
      name: inviteName,
      email: inviteEmail,
      role: inviteRole,
      type: inviteType,
      lastLogin: 'Never',
      status: 'pending'
    };
    setTeamMembers([...teamMembers, newMember]);
    setShowInviteModal(false);
    setInviteName('');
    setInviteEmail('');
    setInviteRole('');
    setInviteType('associated');
  };
  const handleRemoveTeamMember = (memberId: string) => {
    setTeamMembers(teamMembers.filter(m => m.id !== memberId));
  };
  const handleNavigateToRelatedExhibitor = (exhibitorId: string) => {
    if (onNavigateToExhibitor) {
      onNavigateToExhibitor(exhibitorId);
    }
  };
  // Get parent and children exhibitors
  const parentExhibitor = allExhibitors.find(ex => ex.id === exhibitor.parentExhibitorId);
  const childExhibitors = allExhibitors.filter(ex => ex.parentExhibitorId === exhibitor.id);
  const hasRelationships = parentExhibitor || childExhibitors.length > 0;
  const filteredCountries = COUNTRIES.filter(country => country.toLowerCase().includes(countrySearch.toLowerCase()));
  const filteredTerritories = COUNTRIES.filter(country => country !== headquarters && country.toLowerCase().includes(territoriesSearch.toLowerCase()));
  const leadMembers = teamMembers.filter(m => m.type === 'lead');
  const associatedMembers = teamMembers.filter(m => m.type === 'associated');
  // Mock: Check if this exhibitor has parent or children
  const hasParent = exhibitor.id === '1'; // Mock condition
  const hasChildren = exhibitor.id === '1'; // Mock condition
  return <div className="min-h-screen bg-gray-50 -m-8" data-id="element-844">
      {/* Header with Back Button and Exhibitor Name */}
      <div className="bg-white border-b border-gray-200 px-8 py-6" data-id="element-845">
        <div className="flex items-center justify-between" data-id="element-846">
          <div className="flex items-center gap-4" data-id="element-847">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-848">
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-849" />
            </button>
            <div className="flex items-center gap-4" data-id="element-850">
              {exhibitor.logo && <img src={exhibitor.logo} alt={exhibitor.name} className="w-12 h-12 rounded-lg object-cover border border-gray-200" data-id="element-851" />}
              <div data-id="element-852">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-853">
                  {exhibitor.name}
                </h1>
                <p className="text-sm text-gray-500" data-id="element-854">
                  Stand {exhibitor.standNumber}
                </p>
              </div>
            </div>
          </div>

          {/* Progress Indicators */}
          <div className="flex items-center gap-6" data-id="element-855">
            {/* Brand Progress */}
            <div className="flex items-center gap-3" data-id="element-856">
              <div className="text-right" data-id="element-857">
                <div className="text-xs text-gray-500 mb-1" data-id="element-858">Brand Profile</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-859">
                  {brandProfileProgress}%
                </div>
              </div>
              <div className="relative w-12 h-12" data-id="element-860">
                <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48" data-id="element-861">
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#E5E7EB" strokeWidth="4" data-id="element-862" />
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#3B82F6" strokeWidth="4" strokeDasharray={`${2 * Math.PI * 20}`} strokeDashoffset={`${2 * Math.PI * 20 * (1 - brandProfileProgress / 100)}`} strokeLinecap="round" className="transition-all duration-300" data-id="element-863" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-864">
                  <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-865" />
                </div>
              </div>
            </div>

            {/* Onboarding Progress */}
            <div className="flex items-center gap-3" data-id="element-866">
              <div className="text-right" data-id="element-867">
                <div className="text-xs text-gray-500 mb-1" data-id="element-868">Onboarding</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-869">
                  {exhibitorPortalProgress}%
                </div>
              </div>
              <div className="relative w-12 h-12" data-id="element-870">
                <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48" data-id="element-871">
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#E5E7EB" strokeWidth="4" data-id="element-872" />
                  <circle cx="24" cy="24" r="20" fill="none" stroke="#10B981" strokeWidth="4" strokeDasharray={`${2 * Math.PI * 20}`} strokeDashoffset={`${2 * Math.PI * 20 * (1 - exhibitorPortalProgress / 100)}`} strokeLinecap="round" className="transition-all duration-300" data-id="element-873" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-874">
                  <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-875" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Sticky, Full Width, No Gaps */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-10" data-id="element-876">
        <div className="px-8" data-id="element-877">
          <div className="flex gap-8" data-id="element-878">
            <button onClick={() => setActiveTab('overview')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-879">
              Overview
            </button>
            <button onClick={() => setActiveTab('team')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'team' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-880">
              Team Management
            </button>
            <button onClick={() => setActiveTab('passes')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'passes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-881">
              Exhibitor Passes
            </button>
            <button onClick={() => setActiveTab('profile')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'profile' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-882">
              Brand Profile
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-8" data-id="element-883">
        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-884">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-885">
              <Card padding="lg" data-id="element-886">
                <div className="flex items-center justify-between mb-4" data-id="element-887">
                  <div data-id="element-888">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-889">
                      Brand Profile
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-890">
                      {brandProfileProgress}%
                    </div>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg" data-id="element-891">
                    <TrendingUpIcon className="w-6 h-6 text-blue-600" data-id="element-892" />
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-893">
                  <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{
                width: `${brandProfileProgress}%`
              }} data-id="element-894" />
                </div>
                <p className="text-xs text-gray-500 mt-2" data-id="element-895">
                  Profile completion status
                </p>
              </Card>

              <Card padding="lg" data-id="element-896">
                <div className="flex items-center justify-between mb-4" data-id="element-897">
                  <div data-id="element-898">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-899">
                      Exhibitor Portal
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-900">
                      {exhibitorPortalProgress}%
                    </div>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg" data-id="element-901">
                    <FileTextIcon className="w-6 h-6 text-green-600" data-id="element-902" />
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-903">
                  <div className="h-full bg-green-600 rounded-full transition-all duration-300" style={{
                width: `${exhibitorPortalProgress}%`
              }} data-id="element-904" />
                </div>
                <p className="text-xs text-gray-500 mt-2" data-id="element-905">
                  Stand approval documents
                </p>
              </Card>

              <Card padding="lg" data-id="element-906">
                <div className="flex items-center justify-between mb-4" data-id="element-907">
                  <div data-id="element-908">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-909">
                      Last Login
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-910">
                      Sarah Johnson
                    </div>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg" data-id="element-911">
                    <ClockIcon className="w-6 h-6 text-gray-600" data-id="element-912" />
                  </div>
                </div>
                <div className="text-xs text-gray-500" data-id="element-913">2 hours ago</div>
              </Card>

              <Card padding="lg" data-id="element-914">
                <div className="flex items-center justify-between mb-4" data-id="element-915">
                  <div data-id="element-916">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-917">
                      Published
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-918">
                      2 days ago
                    </div>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg" data-id="element-919">
                    <CheckCircle2Icon className="w-6 h-6 text-green-600" data-id="element-920" />
                  </div>
                </div>
                <div className="text-xs text-gray-500" data-id="element-921">Last published</div>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card padding="lg" data-id="element-922">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-923">
                Recent Activity
              </h3>
              <div className="space-y-3" data-id="element-924">
                {mockRecentActivity.map(activity => <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-925">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-926">
                      <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-927" />
                    </div>
                    <div className="flex-1" data-id="element-928">
                      <p className="text-sm text-gray-900" data-id="element-929">
                        <span className="font-medium" data-id="element-930">{activity.user}</span>{' '}
                        {activity.action}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-931">
                        {activity.time}
                      </p>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Parent/Child Relationships Card */}
            {hasRelationships && <Card padding="lg" data-id="element-932">
                <div className="flex items-center gap-2 mb-4" data-id="element-933">
                  <NetworkIcon className="w-5 h-5 text-gray-700" data-id="element-934" />
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-935">
                    Brand Relationships
                  </h3>
                </div>

                {/* Parent Brand */}
                {parentExhibitor && <div className="mb-6" data-id="element-936">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block" data-id="element-937">
                      Parent Brand
                    </label>
                    <button onClick={() => handleNavigateToRelatedExhibitor(parentExhibitor.id)} className="w-full p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group" data-id="element-938">
                      <div className="flex items-center justify-between" data-id="element-939">
                        <div className="flex items-center gap-3" data-id="element-940">
                          {parentExhibitor.logo ? <img src={parentExhibitor.logo} alt={parentExhibitor.name} className="w-12 h-12 rounded object-cover border border-blue-300" data-id="element-941" /> : <div className="w-12 h-12 rounded bg-blue-200 flex items-center justify-center text-sm font-bold text-blue-700" data-id="element-942">
                              {parentExhibitor.name.charAt(0)}
                            </div>}
                          <div className="text-left" data-id="element-943">
                            <div className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-944">
                              {parentExhibitor.name}
                              <Badge variant="default" size="sm" data-id="element-945">
                                Parent
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-600 flex items-center gap-2 mt-1" data-id="element-946">
                              <span className="font-mono text-xs" data-id="element-947">
                                {parentExhibitor.standNumber}
                              </span>
                              <span className="text-gray-400" data-id="element-948">•</span>
                              <span data-id="element-949">
                                {parentExhibitor.headquarters || 'No HQ'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform" data-id="element-950" />
                      </div>
                    </button>
                  </div>}

                {/* Child Brands */}
                {childExhibitors.length > 0 && <div data-id="element-951">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block" data-id="element-952">
                      Child Brands ({childExhibitors.length})
                    </label>
                    <div className="space-y-2" data-id="element-953">
                      {childExhibitors.map(child => <button key={child.id} onClick={() => handleNavigateToRelatedExhibitor(child.id)} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-colors group" data-id="element-954">
                          <div className="flex items-center justify-between" data-id="element-955">
                            <div className="flex items-center gap-3" data-id="element-956">
                              {child.logo ? <img src={child.logo} alt={child.name} className="w-10 h-10 rounded object-cover border border-gray-200" data-id="element-957" /> : <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600" data-id="element-958">
                                  {child.name.charAt(0)}
                                </div>}
                              <div className="text-left" data-id="element-959">
                                <div className="font-medium text-gray-900 text-sm" data-id="element-960">
                                  {child.name}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5" data-id="element-961">
                                  <span className="font-mono" data-id="element-962">
                                    {child.standNumber}
                                  </span>
                                  <span className="text-gray-400" data-id="element-963">•</span>
                                  <span data-id="element-964">{child.headquarters || 'No HQ'}</span>
                                </div>
                              </div>
                            </div>
                            <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" data-id="element-965" />
                          </div>
                        </button>)}
                    </div>
                  </div>}
              </Card>}
          </div>}

        {/* Team Management Tab */}
        {activeTab === 'team' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-966">
            <div className="flex justify-between items-center" data-id="element-967">
              <div data-id="element-968">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-969">
                  Team Management
                </h2>
                <p className="text-sm text-gray-500 mt-1" data-id="element-970">
                  Manage lead and associated team members
                </p>
              </div>
              <Button variant="primary" onClick={() => setShowInviteModal(true)} data-id="element-971">
                <UserPlusIcon className="w-4 h-4 mr-2" data-id="element-972" />
                Invite Member
              </Button>
            </div>

            {/* Lead Team Members */}
            <Card padding="lg" data-id="element-973">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-974">
                Lead Team Members
                <span className="ml-2 text-sm font-normal text-gray-500" data-id="element-975">
                  ({leadMembers.length})
                </span>
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-976">
                Can manage brand profile and invite associated members
              </p>
              <div className="space-y-3" data-id="element-977">
                {leadMembers.map(member => <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-978">
                    <div className="flex-1" data-id="element-979">
                      <div className="flex items-center gap-3" data-id="element-980">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-981">
                          <span className="text-sm font-bold text-blue-600" data-id="element-982">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                        <div data-id="element-983">
                          <div className="flex items-center gap-2" data-id="element-984">
                            <span className="text-sm font-medium text-gray-900" data-id="element-985">
                              {member.name}
                            </span>
                            <Badge variant={member.status === 'active' ? 'success' : 'warning'} size="sm" data-id="element-986">
                              {member.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500" data-id="element-987">
                            {member.email}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-988">{member.role}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-989">
                      <div className="text-right" data-id="element-990">
                        <p className="text-xs text-gray-500" data-id="element-991">Last login</p>
                        <p className="text-xs font-medium text-gray-900" data-id="element-992">
                          {member.lastLogin}
                        </p>
                      </div>
                      <button onClick={() => handleRemoveTeamMember(member.id)} className="text-gray-400 hover:text-red-600 transition-colors" data-id="element-993">
                        <XIcon className="w-4 h-4" data-id="element-994" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Associated Team Members */}
            <Card padding="lg" data-id="element-995">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-996">
                Associated Team Members
                <span className="ml-2 text-sm font-normal text-gray-500" data-id="element-997">
                  ({associatedMembers.length})
                </span>
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-998">
                Can view and contribute to brand profile
              </p>
              <div className="space-y-3" data-id="element-999">
                {associatedMembers.map(member => <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-1000">
                    <div className="flex-1" data-id="element-1001">
                      <div className="flex items-center gap-3" data-id="element-1002">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1003">
                          <span className="text-sm font-bold text-gray-600" data-id="element-1004">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                        <div data-id="element-1005">
                          <div className="flex items-center gap-2" data-id="element-1006">
                            <span className="text-sm font-medium text-gray-900" data-id="element-1007">
                              {member.name}
                            </span>
                            <Badge variant={member.status === 'active' ? 'success' : 'warning'} size="sm" data-id="element-1008">
                              {member.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500" data-id="element-1009">
                            {member.email}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1010">{member.role}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-1011">
                      <div className="text-right" data-id="element-1012">
                        <p className="text-xs text-gray-500" data-id="element-1013">Last login</p>
                        <p className="text-xs font-medium text-gray-900" data-id="element-1014">
                          {member.lastLogin}
                        </p>
                      </div>
                      <button onClick={() => handleRemoveTeamMember(member.id)} className="text-gray-400 hover:text-red-600 transition-colors" data-id="element-1015">
                        <XIcon className="w-4 h-4" data-id="element-1016" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </Card>
          </div>}

        {/* Exhibitor Passes Tab */}
        {activeTab === 'passes' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-1017">
            <Card padding="lg" data-id="element-1018">
              <div className="text-center py-12" data-id="element-1019">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1020">
                  <CreditCardIcon className="w-8 h-8 text-blue-600" data-id="element-1021" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1022">
                  Exhibitor Passes
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-1023">
                  This feature is currently in development. Soon you'll be able
                  to request exhibitor passes for Lead and Associated team
                  members.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto" data-id="element-1024">
                  <p className="text-sm text-blue-800 font-medium mb-2" data-id="element-1025">
                    Coming Soon
                  </p>
                  <p className="text-xs text-blue-700" data-id="element-1026">
                    Request and manage exhibitor passes for all team members
                    across assigned events.
                  </p>
                </div>
              </div>
            </Card>
          </div>}

        {/* Brand Profile Tab - Centered Layout with Sticky Sidebar */}
        {activeTab === 'profile' && <div className="max-w-7xl mx-auto flex gap-8" data-id="element-1027">
            {/* Main Form Content - Centered */}
            <div className="flex-1 max-w-4xl mx-auto space-y-8" data-id="element-1028">
              {/* Company & Stand Details */}
              <Card padding="lg" data-id="element-1029">
                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1030">
                  Company & Stand Details
                </h2>
                <div className="space-y-4" data-id="element-1031">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1032">
                    <div data-id="element-1033">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1034">
                        Legal / Company Name
                      </label>
                      <Input type="text" value={exhibitor.name} disabled className="bg-gray-50" data-id="element-1035" />
                    </div>
                    <div data-id="element-1036">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1037">
                        Stand Number
                      </label>
                      <Input type="text" value={exhibitor.standNumber} disabled className="bg-gray-50" data-id="element-1038" />
                    </div>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1039">
                    <p className="text-sm text-blue-900" data-id="element-1040">
                      <strong data-id="element-1041">Note:</strong> Legal name and stand number cannot
                      be changed here. For updates, please contact{' '}
                      <a href="mailto:support@dubaiactive.com" className="underline" data-id="element-1042">
                        support@dubaiactive.com
                      </a>
                    </p>
                  </div>
                </div>
              </Card>

              {/* Brand Information */}
              <Card padding="lg" data-id="element-1043">
                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1044">
                  Brand Information
                </h2>
                <div className="space-y-6" data-id="element-1045">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1046">
                    <div data-id="element-1047">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1048">
                        Brand Name <span className="text-red-500" data-id="element-1049">*</span>
                      </label>
                      <Input type="text" value={brandName} onChange={e => setBrandName(e.target.value)} placeholder="Enter brand name" data-id="element-1050" />
                    </div>
                    <div data-id="element-1051">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1052">
                        Website <span className="text-red-500" data-id="element-1053">*</span>
                      </label>
                      <Input type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://example.com" data-id="element-1054" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-1055">
                    <div className="relative" data-id="element-1056">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1057">
                        Headquarters <span className="text-red-500" data-id="element-1058">*</span>
                      </label>
                      <div className="relative" data-id="element-1059">
                        <input type="text" value={headquarters} onChange={e => {
                      setHeadquarters(e.target.value);
                      setCountrySearch(e.target.value);
                      setShowCountryDropdown(true);
                    }} onFocus={() => setShowCountryDropdown(true)} placeholder="Select country" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1060" />
                        {showCountryDropdown && <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowCountryDropdown(false)} data-id="element-1061" />
                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-id="element-1062">
                              {filteredCountries.map(country => <button key={country} type="button" onClick={() => {
                          setHeadquarters(country);
                          setShowCountryDropdown(false);
                          setCountrySearch('');
                        }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50" data-id="element-1063">
                                  {country}
                                </button>)}
                            </div>
                          </>}
                      </div>
                    </div>

                    <div className="relative" data-id="element-1064">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1065">
                        Other Territories
                      </label>
                      <div className="relative" data-id="element-1066">
                        <input type="text" value={territoriesSearch} onChange={e => {
                      setTerritoriesSearch(e.target.value);
                      setShowTerritoriesDropdown(true);
                    }} onFocus={() => setShowTerritoriesDropdown(true)} placeholder="Select territories" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1067" />
                        {showTerritoriesDropdown && <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowTerritoriesDropdown(false)} data-id="element-1068" />
                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-id="element-1069">
                              {filteredTerritories.map(country => <button key={country} type="button" onClick={() => {
                          toggleTerritory(country);
                          setTerritoriesSearch('');
                        }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between" data-id="element-1070">
                                  <span data-id="element-1071">{country}</span>
                                  {otherTerritories.includes(country) && <CheckCircleIcon className="w-4 h-4 text-blue-600" data-id="element-1072" />}
                                </button>)}
                            </div>
                          </>}
                      </div>
                      {otherTerritories.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-1073">
                          {otherTerritories.map(territory => <span key={territory} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded" data-id="element-1074">
                              {territory}
                              <button type="button" onClick={() => toggleTerritory(territory)} className="hover:text-blue-900" data-id="element-1075">
                                <XIcon className="w-3 h-3" data-id="element-1076" />
                              </button>
                            </span>)}
                        </div>}
                    </div>
                  </div>

                  <div data-id="element-1077">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1078">
                      Public / Contact Email
                    </label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@example.com" data-id="element-1079" />
                  </div>

                  <div data-id="element-1080">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1081">
                      Brand Overview <span className="text-red-500" data-id="element-1082">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2" data-id="element-1083">
                      Provide a compelling overview of your brand (max 800
                      characters)
                    </p>
                    <textarea value={overview} onChange={e => setOverview(e.target.value)} maxLength={800} rows={6} placeholder="Describe your brand, products, and what makes you unique..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-1084" />
                    <div className="text-xs text-gray-500 text-right mt-1" data-id="element-1085">
                      {overview.length} / 800 characters
                    </div>
                  </div>

                  <div data-id="element-1086">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1087">
                      Industries <span className="text-red-500" data-id="element-1088">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-3" data-id="element-1089">
                      {INDUSTRIES.map(industry => <label key={industry} className="flex items-center gap-2 cursor-pointer" data-id="element-1090">
                          <input type="checkbox" checked={selectedIndustries.includes(industry)} onChange={() => toggleIndustry(industry)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1091" />
                          <span className="text-sm text-gray-700" data-id="element-1092">
                            {industry}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Logo & Assets */}
              <Card padding="lg" data-id="element-1093">
                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1094">
                  Logo & Assets
                </h2>
                <div className="space-y-6" data-id="element-1095">
                  <div data-id="element-1096">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1097">
                      Brand Logo <span className="text-red-500" data-id="element-1098">*</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-3" data-id="element-1099">
                      Minimum 300×300px, square format recommended
                    </p>
                    <div className="flex items-start gap-4" data-id="element-1100">
                      {brandLogo && <img src={brandLogo} alt="Brand logo" className="w-24 h-24 object-contain border border-gray-200 rounded-lg" data-id="element-1101" />}
                      <button type="button" className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2" data-id="element-1102">
                        <UploadIcon className="w-4 h-4" data-id="element-1103" />
                        Upload Logo
                      </button>
                    </div>
                  </div>

                  <div data-id="element-1104">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1105">
                      Brand Logo (Dark Mode)
                    </label>
                    <p className="text-xs text-gray-500 mb-3" data-id="element-1106">
                      Optional: Upload a version optimized for dark backgrounds
                    </p>
                    <button type="button" className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2" data-id="element-1107">
                      <UploadIcon className="w-4 h-4" data-id="element-1108" />
                      Upload Dark Logo
                    </button>
                  </div>

                  <div data-id="element-1109">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1110">
                      Vector Logo Files
                    </label>
                    <p className="text-xs text-gray-500 mb-3" data-id="element-1111">
                      Upload vector files (.svg, .ai, .eps, .pdf) - Max 25MB per
                      file
                    </p>
                    <button type="button" className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2" data-id="element-1112">
                      <UploadIcon className="w-4 h-4" data-id="element-1113" />
                      Upload Vector Files
                    </button>
                  </div>
                </div>
              </Card>

              {/* Social Profiles */}
              <Card padding="lg" data-id="element-1114">
                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1115">
                  Social Profiles
                </h2>
                <div className="space-y-4" data-id="element-1116">
                  <div data-id="element-1117">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1118">
                      Facebook
                    </label>
                    <Input type="url" value={socialFacebook} onChange={e => setSocialFacebook(e.target.value)} placeholder="https://facebook.com/yourpage" data-id="element-1119" />
                  </div>
                  <div data-id="element-1120">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1121">
                      Instagram
                    </label>
                    <Input type="url" value={socialInstagram} onChange={e => setSocialInstagram(e.target.value)} placeholder="https://instagram.com/yourprofile" data-id="element-1122" />
                  </div>
                  <div data-id="element-1123">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1124">
                      LinkedIn
                    </label>
                    <Input type="url" value={socialLinkedin} onChange={e => setSocialLinkedin(e.target.value)} placeholder="https://linkedin.com/company/yourcompany" data-id="element-1125" />
                  </div>
                  <div data-id="element-1126">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1127">
                      YouTube
                    </label>
                    <Input type="url" value={socialYoutube} onChange={e => setSocialYoutube(e.target.value)} placeholder="https://youtube.com/@yourchannel" data-id="element-1128" />
                  </div>
                  <div data-id="element-1129">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1130">
                      TikTok
                    </label>
                    <Input type="url" value={socialTiktok} onChange={e => setSocialTiktok(e.target.value)} placeholder="https://tiktok.com/@yourprofile" data-id="element-1131" />
                  </div>
                </div>
              </Card>

              {/* Declarations */}
              <Card padding="lg" data-id="element-1132">
                <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1133">
                  Declarations
                </h2>
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4" data-id="element-1134">
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1135">
                    By submitting this profile, I confirm that all information
                    provided is accurate and up-to-date. I understand that this
                    information will be displayed publicly on the event platform
                    and in marketing materials. I agree to promptly update any
                    changes to ensure accuracy.
                  </p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-1136">
                  <input type="checkbox" checked={declaration} onChange={e => setDeclaration(e.target.checked)} className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1137" />
                  <span className="text-sm text-gray-700" data-id="element-1138">
                    I confirm that I have read and agree to the above
                    declaration <span className="text-red-500" data-id="element-1139">*</span>
                  </span>
                </label>
              </Card>
            </div>

            {/* Sticky Sidebar - Progress & Actions */}
            <div className="w-80 flex-shrink-0" data-id="element-1140">
              <div className="sticky top-32" data-id="element-1141">
                <Card padding="lg" data-id="element-1142">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-1143">
                    Profile Completion
                  </h3>

                  {/* Circular Progress */}
                  <div className="flex justify-center mb-6" data-id="element-1144">
                    <div className="relative w-32 h-32" data-id="element-1145">
                      <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 128 128" data-id="element-1146">
                        <circle cx="64" cy="64" r="56" fill="none" stroke="#E5E7EB" strokeWidth="8" data-id="element-1147" />
                        <circle cx="64" cy="64" r="56" fill="none" stroke="#2563EB" strokeWidth="8" strokeDasharray={`${2 * Math.PI * 56}`} strokeDashoffset={`${2 * Math.PI * 56 * (1 - brandProfileProgress / 100)}`} strokeLinecap="round" className="transition-all duration-300" data-id="element-1148" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center" data-id="element-1149">
                        <span className="text-3xl font-bold text-gray-900" data-id="element-1150">
                          {brandProfileProgress}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 text-center mb-6" data-id="element-1151">
                    {brandProfileProgress < 100 ? 'Complete all required fields to publish' : 'Profile ready to publish'}
                  </p>

                  {/* Published Status */}
                  <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1152">
                    <div className="flex items-center gap-2 mb-2" data-id="element-1153">
                      <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1154" />
                      <span className="text-sm font-semibold text-gray-900" data-id="element-1155">
                        Published
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500" data-id="element-1156">
                      <ClockIcon className="w-3.5 h-3.5" data-id="element-1157" />
                      <span data-id="element-1158">Last published 2 days ago</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3" data-id="element-1159">
                    <Button variant="outline" onClick={() => setShowPreviewModal(true)} className="w-full" data-id="element-1160">
                      <EyeIcon className="w-4 h-4 mr-2" data-id="element-1161" />
                      Preview
                    </Button>

                    <Button variant="outline" onClick={handleSaveDraft} className="w-full" data-id="element-1162">
                      <SaveIcon className="w-4 h-4 mr-2" data-id="element-1163" />
                      Save Draft
                    </Button>

                    <Button variant="primary" onClick={handlePublish} disabled={isPublishDisabled} className="w-full" data-id="element-1164">
                      <SendIcon className="w-4 h-4 mr-2" data-id="element-1165" />
                      Publish
                    </Button>
                  </div>

                  {isPublishDisabled && <p className="text-xs text-gray-500 text-center mt-3" data-id="element-1166">
                      Complete profile to 100% to publish
                    </p>}
                </Card>
              </div>
            </div>
          </div>}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1167">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col" data-id="element-1168">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-1169">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-1170">
                Profile Preview
              </h3>
              <button onClick={() => setShowPreviewModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1171">
                <XIcon className="w-5 h-5" data-id="element-1172" />
              </button>
            </div>

            {/* Preview Type Tabs */}
            <div className="flex gap-4 px-6 pt-4 border-b border-gray-200" data-id="element-1173">
              <button onClick={() => setPreviewType('website')} className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${previewType === 'website' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1174">
                <MonitorIcon className="w-4 h-4" data-id="element-1175" />
                Website View
              </button>
              <button onClick={() => setPreviewType('app')} className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${previewType === 'app' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1176">
                <SmartphoneIcon className="w-4 h-4" data-id="element-1177" />
                App View
              </button>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-6" data-id="element-1178">
              {previewType === 'website' /* Website Preview */ ? <div className="bg-white border border-gray-200 rounded-lg p-8 max-w-4xl mx-auto" data-id="element-1179">
                  {/* Header */}
                  <div className="flex items-start gap-6 mb-8 pb-8 border-b border-gray-200" data-id="element-1180">
                    {brandLogo && <img src={brandLogo} alt={brandName} className="w-24 h-24 object-contain border border-gray-200 rounded-lg" data-id="element-1181" />}
                    <div className="flex-1" data-id="element-1182">
                      <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1183">
                        {brandName}
                      </h1>
                      <div className="flex flex-wrap gap-2 mb-3" data-id="element-1184">
                        {selectedIndustries.slice(0, 3).map(industry => <Badge key={industry} variant="default" size="sm" data-id="element-1185">
                            {industry}
                          </Badge>)}
                      </div>
                      <div className="flex flex-col gap-2 text-sm text-gray-600" data-id="element-1186">
                        {headquarters && <div className="flex items-center gap-2" data-id="element-1187">
                            <MapPinIcon className="w-4 h-4" data-id="element-1188" />
                            {headquarters}
                          </div>}
                        {website && <div className="flex items-center gap-2" data-id="element-1189">
                            <GlobeIcon className="w-4 h-4" data-id="element-1190" />
                            <a href={website} className="text-blue-600 hover:underline" data-id="element-1191">
                              {website}
                            </a>
                          </div>}
                        {email && <div className="flex items-center gap-2" data-id="element-1192">
                            <EmailIcon className="w-4 h-4" data-id="element-1193" />
                            <a href={`mailto:${email}`} className="text-blue-600 hover:underline" data-id="element-1194">
                              {email}
                            </a>
                          </div>}
                      </div>
                    </div>
                  </div>

                  {/* About */}
                  <div className="mb-8" data-id="element-1195">
                    <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1196">
                      About
                    </h2>
                    <p className="text-gray-700 leading-relaxed" data-id="element-1197">{overview}</p>
                  </div>

                  {/* Industries */}
                  {selectedIndustries.length > 0 && <div className="mb-8" data-id="element-1198">
                      <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1199">
                        Industries
                      </h2>
                      <div className="flex flex-wrap gap-2" data-id="element-1200">
                        {selectedIndustries.map(industry => <Badge key={industry} variant="default" data-id="element-1201">
                            {industry}
                          </Badge>)}
                      </div>
                    </div>}

                  {/* Social Links */}
                  {(socialFacebook || socialInstagram || socialLinkedin || socialYoutube) && <div data-id="element-1202">
                      <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1203">
                        Connect
                      </h2>
                      <div className="flex gap-3" data-id="element-1204">
                        {socialFacebook && <a href={socialFacebook} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-1205">
                            <FacebookIcon className="w-5 h-5 text-gray-700" data-id="element-1206" />
                          </a>}
                        {socialInstagram && <a href={socialInstagram} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-1207">
                            <InstagramIcon className="w-5 h-5 text-gray-700" data-id="element-1208" />
                          </a>}
                        {socialLinkedin && <a href={socialLinkedin} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-1209">
                            <LinkedinIcon className="w-5 h-5 text-gray-700" data-id="element-1210" />
                          </a>}
                        {socialYoutube && <a href={socialYoutube} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-1211">
                            <YoutubeIcon className="w-5 h-5 text-gray-700" data-id="element-1212" />
                          </a>}
                      </div>
                    </div>}
                </div> /* App Preview - Mobile Frame */ : <div className="max-w-sm mx-auto" data-id="element-1213">
                  <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl" data-id="element-1214">
                    <div className="bg-white rounded-[2.5rem] overflow-hidden" data-id="element-1215">
                      {/* Mobile Status Bar */}
                      <div className="bg-gray-50 px-6 py-3 flex items-center justify-between text-xs" data-id="element-1216">
                        <span className="font-semibold" data-id="element-1217">9:41</span>
                        <div className="flex items-center gap-1" data-id="element-1218">
                          <div className="w-4 h-3 border border-gray-400 rounded-sm" data-id="element-1219"></div>
                        </div>
                      </div>

                      {/* App Content */}
                      <div className="bg-white overflow-y-auto" style={{
                  height: '600px'
                }} data-id="element-1220">
                        {/* Header with Logo */}
                        <div className="p-4 border-b border-gray-200" data-id="element-1221">
                          <div className="flex items-center gap-3 mb-3" data-id="element-1222">
                            {brandLogo && <img src={brandLogo} alt={brandName} className="w-16 h-16 object-contain border border-gray-200 rounded-lg" data-id="element-1223" />}
                            <div className="flex-1" data-id="element-1224">
                              <h1 className="text-lg font-bold text-gray-900" data-id="element-1225">
                                {brandName}
                              </h1>
                              <p className="text-xs text-gray-500" data-id="element-1226">
                                Stand {exhibitor.standNumber}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1" data-id="element-1227">
                            {selectedIndustries.slice(0, 2).map(industry => <span key={industry} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded" data-id="element-1228">
                                {industry}
                              </span>)}
                          </div>
                        </div>

                        {/* Contact Info */}
                        <div className="p-4 border-b border-gray-200" data-id="element-1229">
                          <div className="space-y-2 text-sm" data-id="element-1230">
                            {headquarters && <div className="flex items-center gap-2 text-gray-700" data-id="element-1231">
                                <MapPinIcon className="w-4 h-4 flex-shrink-0" data-id="element-1232" />
                                <span data-id="element-1233">{headquarters}</span>
                              </div>}
                            {website && <div className="flex items-center gap-2 text-blue-600" data-id="element-1234">
                                <GlobeIcon className="w-4 h-4 flex-shrink-0" data-id="element-1235" />
                                <span className="truncate" data-id="element-1236">{website}</span>
                              </div>}
                            {email && <div className="flex items-center gap-2 text-blue-600" data-id="element-1237">
                                <EmailIcon className="w-4 h-4 flex-shrink-0" data-id="element-1238" />
                                <span className="truncate" data-id="element-1239">{email}</span>
                              </div>}
                          </div>
                        </div>

                        {/* About */}
                        <div className="p-4 border-b border-gray-200" data-id="element-1240">
                          <h2 className="text-sm font-bold text-gray-900 mb-2" data-id="element-1241">
                            About
                          </h2>
                          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1242">
                            {overview}
                          </p>
                        </div>

                        {/* Industries */}
                        {selectedIndustries.length > 0 && <div className="p-4 border-b border-gray-200" data-id="element-1243">
                            <h2 className="text-sm font-bold text-gray-900 mb-2" data-id="element-1244">
                              Industries
                            </h2>
                            <div className="flex flex-wrap gap-1" data-id="element-1245">
                              {selectedIndustries.map(industry => <span key={industry} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded" data-id="element-1246">
                                  {industry}
                                </span>)}
                            </div>
                          </div>}

                        {/* Social Links */}
                        {(socialFacebook || socialInstagram || socialLinkedin || socialYoutube) && <div className="p-4" data-id="element-1247">
                            <h2 className="text-sm font-bold text-gray-900 mb-3" data-id="element-1248">
                              Connect
                            </h2>
                            <div className="flex gap-2" data-id="element-1249">
                              {socialFacebook && <div className="p-2 bg-gray-100 rounded-lg" data-id="element-1250">
                                  <FacebookIcon className="w-5 h-5 text-gray-700" data-id="element-1251" />
                                </div>}
                              {socialInstagram && <div className="p-2 bg-gray-100 rounded-lg" data-id="element-1252">
                                  <InstagramIcon className="w-5 h-5 text-gray-700" data-id="element-1253" />
                                </div>}
                              {socialLinkedin && <div className="p-2 bg-gray-100 rounded-lg" data-id="element-1254">
                                  <LinkedinIcon className="w-5 h-5 text-gray-700" data-id="element-1255" />
                                </div>}
                              {socialYoutube && <div className="p-2 bg-gray-100 rounded-lg" data-id="element-1256">
                                  <YoutubeIcon className="w-5 h-5 text-gray-700" data-id="element-1257" />
                                </div>}
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200" data-id="element-1258">
              <Button variant="outline" onClick={() => setShowPreviewModal(false)} data-id="element-1259">
                Close
              </Button>
            </div>
          </div>
        </div>}

      {/* Invite Team Member Modal - unchanged */}
      {showInviteModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1260">
          <Card className="w-full max-w-md" data-id="element-1261">
            <div className="p-6" data-id="element-1262">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1263">
                Invite Team Member
              </h3>
              <div className="space-y-4" data-id="element-1264">
                <div data-id="element-1265">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1266">
                    Name *
                  </label>
                  <Input type="text" placeholder="John Doe" value={inviteName} onChange={e => setInviteName(e.target.value)} data-id="element-1267" />
                </div>
                <div data-id="element-1268">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1269">
                    Email *
                  </label>
                  <Input type="email" placeholder="john@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-id="element-1270" />
                </div>
                <div data-id="element-1271">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1272">
                    Role
                  </label>
                  <Input type="text" placeholder="e.g. Marketing Manager" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-1273" />
                </div>
                <div data-id="element-1274">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1275">
                    Member Type *
                  </label>
                  <div className="space-y-2" data-id="element-1276">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-1277">
                      <input type="radio" checked={inviteType === 'lead'} onChange={() => setInviteType('lead')} className="text-blue-600 focus:ring-blue-500" data-id="element-1278" />
                      <div data-id="element-1279">
                        <span className="text-sm font-medium text-gray-900" data-id="element-1280">
                          Lead Team Member
                        </span>
                        <p className="text-xs text-gray-500" data-id="element-1281">
                          Can manage profile and invite others
                        </p>
                      </div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-1282">
                      <input type="radio" checked={inviteType === 'associated'} onChange={() => setInviteType('associated')} className="text-blue-600 focus:ring-blue-500" data-id="element-1283" />
                      <div data-id="element-1284">
                        <span className="text-sm font-medium text-gray-900" data-id="element-1285">
                          Associated Member
                        </span>
                        <p className="text-xs text-gray-500" data-id="element-1286">
                          Can view and contribute to profile
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6" data-id="element-1287">
                <Button variant="outline" onClick={() => setShowInviteModal(false)} className="flex-1" data-id="element-1288">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleInviteTeamMember} disabled={!inviteName || !inviteEmail} data-id="element-1289">
                  <MailIcon className="w-4 h-4 mr-2" data-id="element-1290" />
                  Send Invitation
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}